import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout, AuthNotice } from '../components/AuthLayout.js'
import { Button, Field, Input, PasswordInput } from '../components/ui.js'
import { useSession } from '../lib/session.js'
import { errorMessage, fieldErrors, setBearerToken, useTRPC } from '../lib/trpc.js'

export function AcceptInvite() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const { refresh } = useSession()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')

  const accept = useMutation(
    trpc.auth.acceptInvitation.mutationOptions({
      onSuccess: async (result) => {
        // Accepting signs them straight in. Making someone type the password
        // they just chose into a login form is a pointless extra step.
        setBearerToken(result.token)
        await refresh()
        navigate('/', { replace: true })
      },
    }),
  )

  if (!token) {
    return (
      <AuthLayout title="Invitation not valid">
        <div className="flex flex-col gap-4 text-sm text-ink-600">
          <p>That invitation link is missing its token. Ask the office to send a new one.</p>
          <Link to="/login">
            <Button size="lg" block>
              Back to sign in
            </Button>
          </Link>
        </div>
      </AuthLayout>
    )
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    accept.mutate({ token, password, ...(name.trim() ? { name: name.trim() } : {}) })
  }

  const errors = fieldErrors(accept.error)

  return (
    <AuthLayout title="Set up your account" subtitle="Choose a password to get started.">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field
          label="Your name"
          htmlFor="name"
          hint="Leave blank to keep the name the office entered."
        >
          <Input
            id="name"
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          hint="At least 12 characters."
          error={errors['password']}
          required
        >
          <PasswordInput
            id="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {accept.error ? (
          <AuthNotice tone="error" icon="alert">
            {errorMessage(accept.error)}
          </AuthNotice>
        ) : null}

        <Button type="submit" variant="primary" size="lg" block loading={accept.isPending}>
          Create my account
        </Button>
      </form>
    </AuthLayout>
  )
}
